/// <amd-module name="Environments/Providers/VirtualMachineGroups/Stores/VirtualMachineGroupStore" />
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { VirtualMachineGroupActions } from "Environments/Providers/VirtualMachineGroups/Actions/VirtualMachineGroupActions";
import { VirtualMachine, VirtualMachineGroup } from "DistributedTask/Client/Contracts/TaskAgent";
import { StoreBase, IStoreState } from "Environments/Flux/Store";
import { IVirtualMachineGroupTargetsResult } from "Environments/Providers/VirtualMachineGroups/Services/VirtualMachineGroupExtendedClient";
import { PackageMetadata, TaskAgentPool } from "DistributedTask/Client/Contracts/TaskAgent";
import { Operation, IVirtualMachineTagsInfo } from "Environments/Providers/VirtualMachineGroups/Common/Utils"
import Utils_String = require("VSS/Core/Util/String");
import { arrayEquals } from "VSSUI/Util";

export interface IVirtualMachineGroupStoreState extends IStoreState {
    virtualMachineGroup: VirtualMachineGroup;
    virtualMachineGroupTargetResult: IVirtualMachineGroupTargetsResult;
    patToken: string;
    targetPackages: { [key: string]: PackageMetadata };
    taskAgentPool: TaskAgentPool;
    currentTagsBymachineIds: Map<number, string[]>;
}

export class VirtualMachineGroupStore extends StoreBase {
    public static getKey(): string {
        return "virtualmachinegroup-store";
    }

    public initialize(instanceId: string): void {
        super.initialize(instanceId);
        this._actionsHub = ActionsHubManager.GetActionsHub<VirtualMachineGroupActions>(VirtualMachineGroupActions, instanceId);

        this._actionsHub.setVirtualMachineGroup.addListener(this._setVirtualMachineGroup);
        this._actionsHub.setVirtualMachineGroupTargetsResult.addListener(this._setVirtualMachineGroupTargetsResult);
        this._actionsHub.loadMoreVirtualMachineGroupTargetsResult.addListener(this._loadMoreVirtualMachineGroupTargetsResult);
        this._actionsHub.setPATForDeploymentPool.addListener(this._setPATForDeploymentPool);
        this._actionsHub.setTargetPackages.addListener(this._setTargetPackages);
        this._actionsHub.setTaskAgentPool.addListener(this._setTaskAgentPool);
        this._actionsHub.onVirtualMachineTagUpdated.addListener(this._onVirtualMachineTagUpdated);
        this._actionsHub.onVirtualMachinesUpdated.addListener(this._onVirtualMachinesUpdated);
    }

    public getState(): IVirtualMachineGroupStoreState {
        return this._state;
    }

    public getContinuationToken() {
        return (this.getVirtualMachineGroupTargetsResult() && this.getVirtualMachineGroupTargetsResult().continuationToken)
            ? this.getVirtualMachineGroupTargetsResult().continuationToken : undefined;
    }

    public getVirtualMachineGroup(): VirtualMachineGroup {
        return this.getState().virtualMachineGroup;
    }

    public getVirtualMachineGroupTargetsResult(): IVirtualMachineGroupTargetsResult {
        return this.getState().virtualMachineGroupTargetResult;
    }

    public getcurrentTagsBymachineIds(): Map<number, string[]> {
        return this.getState().currentTagsBymachineIds;
    }

    protected disposeInternal(): void {
        this._actionsHub.setVirtualMachineGroup.removeListener(this._setVirtualMachineGroup);
        this._actionsHub.setVirtualMachineGroupTargetsResult.removeListener(this._setVirtualMachineGroupTargetsResult);
        this._actionsHub.loadMoreVirtualMachineGroupTargetsResult.removeListener(this._loadMoreVirtualMachineGroupTargetsResult);
        this._actionsHub.setPATForDeploymentPool.removeListener(this._setPATForDeploymentPool);
        this._actionsHub.setTargetPackages.removeListener(this._setTargetPackages);
        this._actionsHub.setTaskAgentPool.removeListener(this._setTaskAgentPool);
        this._actionsHub.onVirtualMachineTagUpdated.removeListener(this._onVirtualMachineTagUpdated);
        this._actionsHub.onVirtualMachinesUpdated.removeListener(this._onVirtualMachinesUpdated);
    }

    private _setVirtualMachineGroup = (virtualMachineGroup: VirtualMachineGroup) => {
        this._state.virtualMachineGroup = virtualMachineGroup;
        this.emitChanged();
    }

    private _setVirtualMachineGroupTargetsResult = (virtualMachineGroupTargetResult: IVirtualMachineGroupTargetsResult) => {
        this._state.virtualMachineGroupTargetResult = virtualMachineGroupTargetResult;
        this.emitChanged();
    }

    private _loadMoreVirtualMachineGroupTargetsResult = (virtualMachineGroupTargetResult: IVirtualMachineGroupTargetsResult) => {

        let currentVMGroupTargetsResult = this._state.virtualMachineGroupTargetResult;
        currentVMGroupTargetsResult.virtualMachines.push(...virtualMachineGroupTargetResult.virtualMachines);
        currentVMGroupTargetsResult.continuationToken = virtualMachineGroupTargetResult.continuationToken;

        this._state.virtualMachineGroupTargetResult = currentVMGroupTargetsResult;
        this.emitChanged();
    }

    private _setPATForDeploymentPool = (patToken: string) => {
        this._state.patToken = patToken;
        this.emit(Operation.GetPATToken, this);
    }

    private _setTargetPackages = (targetPackages: PackageMetadata[]) => {
        for (let targetPkg of targetPackages) {
            this._state.targetPackages[targetPkg.platform] = targetPkg;
        }
        this.emit(Operation.GetTargetPackages, this);
    }

    private _setTaskAgentPool = (taskAgentPool: TaskAgentPool) => {
        this._state.taskAgentPool = taskAgentPool;
        this.emit(Operation.GetTaskAgentPool, this);
    }

    private _onVirtualMachinesUpdated = (virtualMachines: VirtualMachine[]): void => {
        let virtualMachineGroupData =this.getState();
        let updatedVirtualMachinesMap: any = {};
        for(let virtualMachine of virtualMachines) {
            updatedVirtualMachinesMap[virtualMachine.id] = virtualMachine;
        }

        for(let i=0; i < virtualMachineGroupData.virtualMachineGroupTargetResult.virtualMachines.length ; i++) {
            let virtualMachine = virtualMachineGroupData.virtualMachineGroupTargetResult.virtualMachines[i];
            if(updatedVirtualMachinesMap[virtualMachine.id]) {
                virtualMachineGroupData.virtualMachineGroupTargetResult.virtualMachines[i].tags = updatedVirtualMachinesMap[virtualMachine.id].tags;
            }
        }

        if(!!virtualMachineGroupData.currentTagsBymachineIds) {
            virtualMachineGroupData.currentTagsBymachineIds.clear();
        }

        this.emitChanged();
    }

    private _onVirtualMachineTagUpdated = (virtualMachineInfo: IVirtualMachineTagsInfo): void => {
        let virtualMachineGroupData =this.getState();
        let virtualMachineId = virtualMachineInfo.machine.id;
        let tags = virtualMachineInfo.machine.tags || [];
        let updatedTags = virtualMachineInfo.tags;
        
        if(arrayEquals(tags, updatedTags, (a, b) => Utils_String.localeIgnoreCaseComparer(a, b) === 0)) {
            virtualMachineGroupData.currentTagsBymachineIds.delete(virtualMachineId);    
        } else {
            virtualMachineGroupData.currentTagsBymachineIds.set(virtualMachineId, updatedTags);
        }

        this._state.currentTagsBymachineIds = virtualMachineGroupData.currentTagsBymachineIds;
        this.emitChanged();
    }

    private _actionsHub: VirtualMachineGroupActions;
    private _state: IVirtualMachineGroupStoreState = {
        targetPackages: {},
        currentTagsBymachineIds: new Map<number, string[]>()
    } as IVirtualMachineGroupStoreState;
}